import { InlineMathNode } from '@aarkue/tiptap-math-extension';
import { Extension } from '@tiptap/core';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import TextStyle from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Editor, ReactNodeViewRenderer } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { prosemirrorJSONToYXmlFragment, yXmlFragmentToProsemirrorJSON } from 'y-prosemirror';
import * as Y from 'yjs';
import type { XmlFragment as YXmlFragment } from 'yjs';

import { Audio } from './extensions/Audio/Audio';
import { InlineMathNodeView } from './extensions/InlineMathNodeView';
import { ListStylePropagation } from './extensions/ListStylePropagation';
import { BaseTooltipMark } from './extensions/Tooltip/Tooltip';
import { WrapContentInSpan } from './extensions/WrapContentInSpan';
import { CustomLink } from './extensions/customLink';
import { FontFamily } from './extensions/fontFamily';
import { FontSize } from './extensions/fontSize';
import { Heading } from './extensions/heading';
import { Image } from './extensions/image';
import { LineHeight } from './extensions/lineHeight';
import { TextDirection } from './extensions/textDirection';
import { KATEX_MACROS } from '../math/katexMacros';
import { extractPastedLatex } from '../math/mathClipboard/mathClipboard';
import { INLINE_MATH_NODE_NAME } from '../math/mathNode';

// Extend the math extension's inline node with a React node view that renders KaTeX and
// opens a mathlive popover on click, so rendered formulas can be edited in place.
export const EditableInlineMath = InlineMathNode.extend({
  // Drop the base extension's Backspace handler, which decompiles a formula back to raw `$latex$`
  // text on delete. We edit via the click-to-edit popover, so let the default keymap delete the node.
  addKeyboardShortcuts() {
    return {};
  },
  addNodeView() {
    return ReactNodeViewRenderer(InlineMathNodeView);
  }
});

/**
 * Convert a formula copied from a mathlive `<math-field>` into a rendered `inlineMath` node on
 * paste. The math extension's own paste rule can't: its `$$…$$` regex rejects mathlive's spaced
 * delimiters, so the formula would otherwise land as raw `$$ … $$` text (bug 529112).
 *
 * This is a STANDALONE extension with a high `priority` for a reason. Tiptap reverses the extension
 * list when assembling plugins, and ProseMirror's `handlePaste` is first-wins. The Cloze List /
 * Fill-in-the-blanks editors register their own `handlePaste` (via `handlePasteLogic`) that
 * *always* returns `true`, so on those editors a plugin defined on the math node would be consulted
 * *after* theirs and never run. A high priority guarantees this handler is consulted first; it
 * returns `false` for anything that isn't a single mathlive formula, so those editors' handlers
 * still process everything else.
 */
export const MathLivePaste = Extension.create({
  name: 'mathLivePaste',
  // Must out-rank the Cloze / Fill-in-the-blanks paste handlers (default priority 100), which
  // unconditionally consume every paste.
  priority: 1000,
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('mathLivePaste'),
        props: {
          handlePaste: (view, event) => {
            const latex = extractPastedLatex(event.clipboardData);
            if (!latex) return false;

            const { state } = view;
            const node = state.schema.nodes[INLINE_MATH_NODE_NAME].create({ latex });
            // Only insert where an inline math node is actually allowed. In a `codeBlock` (content
            // `text*`) it isn't, and `replaceSelectionWith` would throw a ProseMirror replace error;
            // returning false lets the default paste handling run instead.
            const { $from, $to } = state.selection;
            if (!$from.parent.canReplaceWith($from.index(), $to.index(), node.type)) return false;

            view.dispatch(state.tr.replaceSelectionWith(node).scrollIntoView());
            return true;
          }
        }
      })
    ];
  }
});

type GetDefaultExtensionsOptions = {
  useNativeHistory?: boolean;
};

export const getDefaultExtensions = (options?: GetDefaultExtensionsOptions) => {
  const { useNativeHistory = true } = options ?? {};
  return [
    Color,
    CustomLink,
    FontFamily,
    FontSize,
    LineHeight,
    Highlight.configure({
      multicolor: true
    }),
    Image,
    Audio,
    EditableInlineMath.configure({
      evaluation: false,
      delimiters: 'dollar',
      katexOptions: { macros: KATEX_MACROS }
    }),
    MathLivePaste,
    useNativeHistory
      ? StarterKit.configure({ heading: false })
      : StarterKit.configure({ heading: false, history: false }),
    TextDirection.configure({
      types: ['heading', 'paragraph']
    }),
    WrapContentInSpan,
    ListStylePropagation,
    TextStyle,
    Underline,
    BaseTooltipMark,
    Heading
  ];
};

export const htmlToXmlFragment = (content: string) => {
  const editor = new Editor({
    extensions: getDefaultExtensions(),
    content
  });

  const schema = editor.schema;
  const jsonContent = editor.getJSON();

  return prosemirrorJSONToYXmlFragment(schema, jsonContent);
};

export const yXmlFragmentToText = (fragment: YXmlFragment) => {
  const content = yXmlFragmentToProsemirrorJSON(fragment);
  const editor = new Editor({
    extensions: getDefaultExtensions(),
    content
  });

  return editor.getText();
};

export const isFragmentHasContent = (fragment?: YXmlFragment) => {
  if (!fragment) return false;

  let hasContent = false;
  const visit = (node: Y.XmlElement | Y.XmlText | Y.XmlHook) => {
    if (hasContent) return;
    if (node instanceof Y.XmlText) {
      if (node.toString().trim().length > 0) hasContent = true;
    } else if (node instanceof Y.XmlElement) {
      // any non-paragraph element (image, math, embed, …) is content on its own
      if (node.nodeName !== 'paragraph') {
        hasContent = true;
      } else {
        node.toArray().forEach(visit);
      }
    }
  };
  fragment.toArray().forEach(visit);

  return hasContent;
};
